import { memo, useCallback, useState, useRef, useEffect } from 'react'
import VideoDoctorIcon from 'assets/icons/VideoDoctor'
import DoubleTick from 'assets/icons/DoubleTick'
import Bell from 'assets/icons/Bell'
import ClinicIcon from 'assets/icons/ClinicDoctor'
import CameraOn from 'assets/icons/communication/CameraOn'
import { CS, CS_m } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import { purple, primary, green } from 'styles/Colors'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { useReadOnly } from 'components/patient/ReadOnlyContext'
import { useMiscDispatch } from 'services/Misc'
import { useAudioVisualsState, setAppointmentPickedUp } from 'services/Misc/audioVisuals'
import useIsAssistant from 'hooks/useIsAssistant'
import styled, { css, keyframes } from 'styled-components'
import AdviceForm from './AdviceForm'
import AppointmentSchedule from './AppointmentSchedule'
import Urgent from './Urgent'
import Referral from './Referral'
import Modal from './Modal'

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    avTooltip: getText('AV Call', 'Patient_Details_Actions'),
    adviceTooltip: getText('Advice', 'Patient_Details_Actions'),
    referralTooltip: getText('Referral', 'Patient_Details_Actions'),
    callInProgress: getText('Call in progress - cannot start new call', 'Patient_Details_Actions'),
    pickUpCall: getText('Pick up call', 'Patient_Details_Actions'),
    directAv: getText('Direct AV Call', 'Patient_Details_Actions'),
    scheduleAv: getText('Schedule AV Call', 'Patient_Details_Actions'),
  }[key])

/* ── Styled tab button ── */

const TabButton = styled.button<{ color: string; disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-family: coresanscr65, sans-serif;
  font-size: 0.8125rem;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  color: ${({ color }) => color};
  background: ${({ color }) => `linear-gradient(135deg, ${color}18 0%, ${color}30 100%)`};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ color }) => `linear-gradient(135deg, ${color}28 0%, ${color}45 100%)`};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${({ color }) => `${color}25`};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`

/* ── Glow animation for the "Pick up call" button ── */

const pickUpGlow = keyframes`
  0%, 100% {
    box-shadow:
      0 0 0 0 rgba(51, 184, 151, 0.55),
      0 0 12px 2px rgba(51, 184, 151, 0.35);
    background: linear-gradient(135deg, rgba(51, 184, 151, 0.15) 0%, rgba(51, 184, 151, 0.35) 100%);
  }
  50% {
    box-shadow:
      0 0 0 12px rgba(51, 184, 151, 0),
      0 0 24px 6px rgba(51, 184, 151, 0.65);
    background: linear-gradient(135deg, rgba(51, 184, 151, 0.30) 0%, rgba(51, 184, 151, 0.55) 100%);
  }
`

const PickUpTabButton = styled(TabButton)`
  animation: ${pickUpGlow} 1.8s ease-in-out infinite;
`

/* ── AV Call split animation ── */

const splitLeft = keyframes`
  0% { transform: translateX(0); opacity: 0; max-width: 0; padding: 0; margin: 0; }
  100% { transform: translateX(0); opacity: 1; max-width: 200px; padding: 10px 16px; margin: 0; }
`

const collapseOut = keyframes`
  0% { opacity: 1; max-width: 200px; }
  100% { opacity: 0; max-width: 0; padding: 0; margin: 0; overflow: hidden; }
`

const AvSplitTab = styled(TabButton)<{ animating: boolean }>`
  ${({ animating }) =>
    animating &&
    css`
      animation: ${collapseOut} 0.25s ease forwards;
    `}
`

const SplitOption = styled(TabButton)<{ entering: boolean }>`
  overflow: hidden;
  ${({ entering }) =>
    entering
      ? css`
          animation: ${splitLeft} 0.3s ease 0.15s both;
        `
      : css`
          animation: ${collapseOut} 0.2s ease forwards;
        `}
`

const TabRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

enum SideBar {
  advice,
  scheduleAv,
  directAv,
  referral,
}

const Actions = () => {
  const { registration, summary, appointment } = usePatientRouteParams()
  const audioVisualState = useAudioVisualsState()
  const miscDispatch = useMiscDispatch()
  const readOnly = useReadOnly()
  const isAssistant = useIsAssistant()

  const { t } = useTranslation(texts)
  const [avExpanded, setAvExpanded] = useState(false)
  const [avCollapsing, setAvCollapsing] = useState(false)
  const [sideBar, setSideBar] = useState<SideBar | null>(null)
  const avContainerRef = useRef<HTMLDivElement>(null)

  // Collapse AV split when clicking outside
  useEffect(() => {
    if (!avExpanded) return undefined
    const handleClickOutside = (e: MouseEvent) => {
      if (avContainerRef.current && !avContainerRef.current.contains(e.target as Node)) {
        setAvCollapsing(true)
        setTimeout(() => {
          setAvExpanded(false)
          setAvCollapsing(false)
        }, 250)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [avExpanded])

  const hasActiveCall =
    audioVisualState.pickedUpAppointmentId !== null ||
    audioVisualState.inProgressAppointmentId !== null

  const hasPendingOrAcceptedAppointment =
    appointment &&
    (appointment.status === CS_m.ModelsAppointmentStatus.pending ||
      appointment.status === CS_m.ModelsAppointmentStatus.accepted)

  const avCallDisabled =
    readOnly ||
    CS.isSummaryStatusFinal(summary) ||
    hasActiveCall ||
    hasPendingOrAcceptedAppointment ||
    false

  const canPickUp =
    !readOnly &&
    appointment &&
    (appointment.status === CS_m.ModelsAppointmentStatus.ready ||
      appointment.status === CS_m.ModelsAppointmentStatus.inProgress) &&
    !hasActiveCall

  const onPickup = useCallback(() => {
    if (appointment && canPickUp) {
      setAppointmentPickedUp(miscDispatch, audioVisualState, appointment)
    }
  }, [miscDispatch, audioVisualState, appointment, canPickUp])

  const setSidebar = useCallback(
    (activeSideBar: SideBar | null) => () => {
      setSideBar(activeSideBar)
      setAvExpanded(false)
      setAvCollapsing(false)
    },
    []
  )

  const handleAvClick = useCallback(() => {
    if (avCallDisabled) return
    if (avExpanded) {
      // collapse back
      setAvCollapsing(true)
      setTimeout(() => {
        setAvExpanded(false)
        setAvCollapsing(false)
      }, 250)
    } else {
      setAvExpanded(true)
    }
  }, [avCallDisabled, avExpanded])

  const adviceDisabled = isAssistant || readOnly || (CS.isSummaryStatusFinal(summary) && !summary?.advice?.text)
  // @ts-ignore — referral_special not yet in generated ModelsSummary type
  const referralDisabled = isAssistant || readOnly || (CS.isSummaryStatusFinal(summary) && !summary?.referral?.url && !summary?.referral_special?.url)

  return (
    <>
      <TabRow>
        {summary?.product === CS_m.ModelsSummaryProduct.videoCall && (
          <div ref={avContainerRef} style={{ display: 'contents' }}>
            {!avExpanded && (
              <AvSplitTab
                color={purple}
                disabled={avCallDisabled}
                onClick={handleAvClick}
                animating={avExpanded}
              >
                <VideoDoctorIcon className={undefined} />
                {hasActiveCall ? t('callInProgress') : t('avTooltip')}
              </AvSplitTab>
            )}
            {avExpanded && (
              <>
                <SplitOption
                  color={purple}
                  entering={!avCollapsing}
                  onClick={setSidebar(SideBar.directAv)}
                >
                  <CameraOn style={undefined} color={purple} />
                  {t('directAv')}
                </SplitOption>
                <SplitOption
                  color={purple}
                  entering={!avCollapsing}
                  onClick={setSidebar(SideBar.scheduleAv)}
                >
                  <VideoDoctorIcon className={undefined} />
                  {t('scheduleAv')}
                </SplitOption>
              </>
            )}
          </div>
        )}

        {canPickUp && (
          <PickUpTabButton color={green} onClick={onPickup}>
            <CameraOn style={undefined} color={green} />
            {t('pickUpCall')}
          </PickUpTabButton>
        )}

        <TabButton color={primary} disabled={adviceDisabled} onClick={setSidebar(SideBar.advice)}>
          <ClinicIcon className={undefined} />
          {t('adviceTooltip')}
        </TabButton>

        <TabButton color={green} disabled={referralDisabled} onClick={setSidebar(SideBar.referral)}>
          {/* @ts-ignore — referral_special not yet in generated ModelsSummary type */}
          {!summary?.referral?.url && !summary?.referral_special?.url ? (
            <Bell color={green} />
          ) : (
            <DoubleTick color={green} />
          )}
          {t('referralTooltip')}
        </TabButton>
      </TabRow>

      <Modal visible={sideBar !== null}>
        {(() => {
          switch (sideBar) {
            case SideBar.advice:
              return <AdviceForm onClose={setSidebar(null)} registration={registration} />
            case SideBar.referral:
              return <Referral onClose={setSidebar(null)} />
            case SideBar.scheduleAv:
              return <AppointmentSchedule summaryId={summary?.id} onClose={setSidebar(null)} />
            case SideBar.directAv:
              return <Urgent summaryId={summary?.id} onClose={setSidebar(null)} />
            case null:
              return null
            default:
              console.error('Unhandled Diagnosis actions sideBar', sideBar)
              return null
          }
        })()}
      </Modal>
    </>
  )
}

Actions.whyDidYouRender = true

export default memo(Actions)
